import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { runOwsRequestWithMonitor, DistributionListMember, Feature } from '../../../shared';
import {
    CreatePersonaRequest,
    TargetFolderId,
    PersonaType,
    getServiceAsync,
    TFactories,
} from '../../../service';

export type CreatePrivateDLData = {
    newName: string;
    newNotes?: string;
    newMembers: DistributionListMember[];
    parentFolderId: string;
};

export function createPrivateDL(
    data: CreatePrivateDLData
): Promise<{ personaId: string; itemId: string }> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: 'CreatePrivateDL',
            feature: Feature.PrivateDistributionList,
            operation: () => {
                return service.Operation.createPersonaOperation({
                    request: {
                        Header: getJsonRequestHeader(),
                        Body: buildRequest(data, service.Factory),
                    },
                });
            },
            transformResponse: (personaType: PersonaType) => {
                return {
                    personaId: personaType.PersonaId.Id,
                    itemId: personaType.EmailAddress.ItemId.Id,
                };
            },
        })
    );
}

function buildRequest(data: CreatePrivateDLData, serviceFactory: TFactories): CreatePersonaRequest {
    const parentFolderId = data.parentFolderId;
    const targetFolderID = serviceFactory.targetFolderId({
        BaseFolderId: serviceFactory.folderId({ Id: parentFolderId }),
    }) as TargetFolderId;

    const request = serviceFactory.createPersonaRequest({
        PersonaId: null,
        PersonTypeString: 'DistributionList',
        PropertyUpdates: [],
        ParentFolderId: targetFolderID !== undefined ? targetFolderID : undefined,
    });

    request.PropertyUpdates.push(
        serviceFactory.personaPropertyUpdate({
            Path: serviceFactory.propertyUri({ FieldURI: 'PersonaDisplayNames' }),
            OldValue: '',
            NewValue: data.newName,
        })
    );

    if (data.newNotes) {
        request.PropertyUpdates.push(
            serviceFactory.personaPropertyUpdate({
                Path: serviceFactory.propertyUri({ FieldURI: 'PersonaBodies' }),
                OldValue: '',
                NewValue: data.newNotes,
            })
        );
    }

    data.newMembers.forEach(newMember => {
        if (newMember.isManuallyTypedEmail) {
            // Manually typed email address
            request.PropertyUpdates.push(
                serviceFactory.personaPropertyUpdate({
                    Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                    AddMemberToPDL: {
                        Name: newMember.displayName,
                        EmailAddress: newMember.emailAddressWrapper.EmailAddress,
                    },
                })
            );
        } else {
            // Person or a PDL added via the PeoplePicker via "Edit" a PDL
            request.PropertyUpdates.push(
                serviceFactory.personaPropertyUpdate({
                    Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                    AddMemberToPDL: newMember.emailAddressWrapper,
                })
            );
        }
    });

    return request;
}
